import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Progress, Table, Typography } from 'antd';
// Api
import { GET_CHANNEL_MONETIZATION_LEADERBOARD_ADMIN } from 'api/channelMonetization/queries';
// Types
import {
  GetChannelMonetizationLeaderboardAdmin,
  GetChannelMonetizationLeaderboardAdminVariables,
  GetChannelMonetizationLeaderboardAdmin_getChannelMonetizationLeaderboardAdmin_entities as LeaderboardEntry,
} from 'api/channelMonetization/types/GetChannelMonetizationLeaderboardAdmin';
// Components
import CreatorProgressDrawer from './components/CreatorProgressDrawer/CreatorProgressDrawer';
// Styles
import styles from './ChannelMonetizationLeaderboard.module.scss';

const { Text, Title } = Typography;
const PAGE_SIZE = 50;

const entryName = (record: LeaderboardEntry): string =>
  record.storeName || record.storeId;

const ChannelMonetizationLeaderboard: React.FC = () => {
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<{
    storeId: string;
    name: string;
  } | null>(null);

  const { data, loading } = useQuery<
    GetChannelMonetizationLeaderboardAdmin,
    GetChannelMonetizationLeaderboardAdminVariables
  >(GET_CHANNEL_MONETIZATION_LEADERBOARD_ADMIN, {
    variables: {
      input: {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const result = data?.getChannelMonetizationLeaderboardAdmin;
  const entries = result?.entities ?? [];
  const total = result?.total ?? 0;
  const monthLabel = result?.monthLabel;

  const columns = [
    {
      title: '#',
      key: 'rank',
      width: 70,
      render: (_: unknown, __: LeaderboardEntry, index: number) =>
        (page - 1) * PAGE_SIZE + index + 1,
    },
    {
      title: 'Creator',
      key: 'creator',
      width: 280,
      render: (_: unknown, record: LeaderboardEntry) => (
        <div className={styles.creator}>
          <div
            className={styles.avatar}
            style={
              record.avatarUrl
                ? { backgroundImage: `url(${record.avatarUrl})` }
                : undefined
            }
          />
          <span className={styles.creatorName}>{entryName(record)}</span>
        </div>
      ),
    },
    {
      title: 'Progress',
      key: 'progress',
      width: 260,
      render: (_: unknown, record: LeaderboardEntry) => (
        <Progress
          percent={record.overallPercent}
          size="small"
          status="active"
        />
      ),
    },
    {
      title: 'Completed Steps',
      key: 'completedSteps',
      width: 160,
      render: (_: unknown, record: LeaderboardEntry) =>
        `${record.completedSteps} / ${record.totalSteps}`,
    },
  ];

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Title level={4} className={styles.title}>
          Channel Monetization Leaderboard
        </Title>
        {monthLabel ? (
          <Text type="secondary">Current month: {monthLabel}</Text>
        ) : (
          <Text type="secondary">No active program</Text>
        )}
      </div>

      <Table<LeaderboardEntry>
        rowKey="storeId"
        columns={columns}
        dataSource={entries}
        loading={loading}
        scroll={{ x: 800 }}
        onRow={(record) => ({
          onClick: () =>
            setSelected({ storeId: record.storeId, name: entryName(record) }),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          onChange: setPage,
          showSizeChanger: false,
        }}
      />

      <CreatorProgressDrawer
        storeId={selected?.storeId ?? null}
        monthLabel={monthLabel ?? null}
        creatorName={selected?.name ?? ''}
        open={!!selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
};

export default ChannelMonetizationLeaderboard;
